import { Button, Container } from '@mui/material';
import classNames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { formatManwon } from '../../utils/formatKRW';
import { logEvent } from '../../utils/ga';
import Adsense from '../Ads/Adsense';
import { ExpertCallout } from '../shared/ExpertCallout';
import { PensionChart } from './Chart/PensionChart';
import { PensionForm } from './Form/PensionForm';
import { calculatePension, getPensionCallout, PensionFormValues, PensionResult } from './PensionPage.hooks';
import styles from './PensionPage.module.css';

function PensionPage() {
  const navigate = useNavigate();
  const resultRef = useRef<HTMLDivElement>(null);
  const [result, setResult] = useState<PensionResult>();
  const [values, setValues] = useState<PensionFormValues>();

  const handleSubmit = useCallback((nextValues: PensionFormValues) => {
    logEvent('calculator', 'pension_calculate', 'submit');
    setValues(nextValues);
    setResult(calculatePension(nextValues));
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, []);

  const callout = useMemo(() => (result ? getPensionCallout(result) : null), [result]);

  return (
    <div className={styles.root}>
      <Helmet>
        <title>국민연금 계산기 — 예상 수령액 확인 | 노후든든</title>
        <meta
          name="description"
          content="국민연금 예상 수령액을 간단히 계산하세요. 납입 기간과 소득 기준으로 추정한 월 연금액과 소득 대체율을 확인할 수 있습니다."
        />
      </Helmet>

      <Container maxWidth="sm">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>국민연금 계산기</h1>
          <p className={styles.pageDesc}>
            납입 기간과 소득 기준으로 예상 월 연금액과 소득 대체율을 빠르게 확인하세요.
          </p>
        </div>
        <div className={styles.formCard}>
          <PensionForm onSubmit={handleSubmit} />
        </div>
      </Container>

      {result && values && (
        <div ref={resultRef}>
          <Container maxWidth="md" sx={{ mt: 4 }}>
            <div className={styles.resultSection}>
              <div
                className={classNames(styles.resultHeroCard, {
                  [styles.heroSuccess]: result.isEligible && result.replacementRate >= 50,
                  [styles.heroWarning]: !result.isEligible,
                })}
              >
                <p className={styles.resultHeroLabel}>
                  {result.isEligible ? '예상 월 국민연금 수령액' : '납부 기간 부족 — 수령 자격 없음'}
                </p>
                <p className={styles.resultHeroAmount}>
                  {result.isEligible ? formatManwon(result.estimatedMonthlyPension) : '0원'}
                </p>
                <p className={styles.resultHeroSub}>
                  {result.isEligible
                    ? `만 ${result.pensionStartAge}세 수급 개시 기준 · 연 ${formatManwon(result.annualPension)}`
                    : '10년 이상 납부해야 연금으로 받을 수 있습니다'}
                </p>
              </div>

              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>소득 대체율</p>
                  <p className={styles.metricValue}>{result.replacementRate.toFixed(1)}%</p>
                  <p className={styles.metricInterpretation}>내 소득 대비 연금 비율</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>85세까지 수령 총액</p>
                  <p className={styles.metricValue}>{formatManwon(result.totalLifetimePension)}</p>
                  <p className={styles.metricInterpretation}>기대여명 기준 누계</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>예상 납부 기간</p>
                  <p className={styles.metricValue}>{result.contributionYears}년</p>
                  <p className={styles.metricInterpretation}>총 납부 인정 기간</p>
                </div>
              </div>

              <div className={styles.chartCard}>
                <p className={styles.chartTitle}>월 소득 vs 예상 연금 수령액</p>
                <div className={styles.chartWrapper}>
                  <PensionChart
                    averageMonthlyIncome={values.averageMonthlyIncome}
                    estimatedMonthlyPension={result.estimatedMonthlyPension}
                  />
                </div>
              </div>

              {callout && (
                <ExpertCallout title={callout.title} body={callout.body} cta={callout.cta} />
              )}

              <div className={styles.ctaRow}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    logEvent('engagement', 'pension_cta_click', '/retirement');
                    navigate('/retirement');
                  }}
                  sx={{ background: '#000033', '&:hover': { background: '#001166' } }}
                >
                  노후 자금 계산기에 반영하기 →
                </Button>
              </div>

              <Adsense adClient="ca-pub-9359050149154028" adSlot="PENSION_AD_SLOT" />

              <p className={styles.disclaimer}>
                이 계산기는 투자 권유가 아닌 참고용 시뮬레이션입니다. 실제 수익은 시장 상황에 따라 다르며,
                세금·수수료·물가 상승률은 반영되지 않았습니다. 중요한 재무 결정은 전문가와 상담하세요.
                이 계산기는 2024년 국민연금 공식을 단순화한 추정치입니다. 정확한 수령액은 국민연금공단(nps.or.kr)에서 확인하세요.
              </p>
            </div>
          </Container>
        </div>
      )}
    </div>
  );
}

export default PensionPage;
